import React, { useState } from 'react';
import {
  Box, Button, Input, VStack, Heading, Text, useToast, Container, Flex, InputGroup,
  InputLeftElement, Icon, PinInput, PinInputField, HStack, Link,
} from '@chakra-ui/react';
import { FiUser, FiLock, FiShield } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import { APP_VERSION } from '../version';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const { login } = useAuth();
  const toast = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password, mfaRequired ? mfaCode : null);
      if (result?.mfa_required) {
        setMfaRequired(true);
        toast({ title: 'Enter your authenticator code', status: 'info', duration: 3000 });
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Invalid credentials';
      if (err.response?.data?.mfa_failed) {
        toast({ title: 'Invalid MFA code', description: 'Please check your authenticator app', status: 'error', duration: 3000 });
      } else {
        toast({ title: 'Login failed', description: msg, status: 'error', duration: 3000 });
        setMfaRequired(false); setMfaCode('');
      }
    } finally { setLoading(false); }
  };

  const handleResetRequest = async () => {
    if (!resetEmail) { toast({ title: 'Enter your email', status: 'warning', duration: 2000 }); return; }
    try {
      await api.post('/auth/request-reset', { email: resetEmail });
      setResetSent(true);
      toast({ title: 'If an account exists, a reset link has been sent', status: 'success', duration: 4000 });
    } catch(e) { toast({ title: 'Error', status: 'error', duration: 2000 }); }
  };

  if (showReset) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="gray.50">
        <Container maxW="sm">
          <Box bg="white" p={8} borderRadius="xl" boxShadow="lg" border="1px" borderColor="gray.100">
            <VStack spacing={4}>
              <Heading size="md" color="gray.700">Reset Password</Heading>
              {resetSent ? (
                <>
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    If an account exists with that email, a reset link has been sent. Check your inbox.
                  </Text>
                  <Button size="sm" variant="ghost" onClick={() => { setShowReset(false); setResetSent(false); setResetEmail(''); }}>
                    Back to Login
                  </Button>
                </>
              ) : (
                <>
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    Enter your email address and we'll send you a link to reset your password.
                  </Text>
                  <Input placeholder="Email address" value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleResetRequest(); }} />
                  <Button w="100%" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }} onClick={handleResetRequest}>Send Reset Link</Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowReset(false)}>Back to Login</Button>
                </>
              )}
            </VStack>
          </Box>
        </Container>
      </Flex>
    );
  }

  return (
    <Flex minH="100vh" align="center" justify="center" bg="gray.50">
      <Container maxW="sm">
        <Box bg="white" p={8} borderRadius="xl" boxShadow="lg" border="1px" borderColor="gray.100">
          <VStack spacing={6} as="form" onSubmit={handleSubmit}>
            <Box textAlign="center">
              <Heading size="lg" color="brand.700" fontWeight="700">ForgeBOS</Heading>
              <Text fontSize="sm" color="gray.500" mt={1}>Run everything from one Business Operating System.</Text>
              <Text fontSize="9px" color="gray.400" mt={1}>v{APP_VERSION}</Text>
            </Box>

            <VStack spacing={3} w="100%">
              {!mfaRequired ? (
                <>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none"><Icon as={FiUser} color="gray.400" /></InputLeftElement>
                    <Input type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} autoFocus />
                  </InputGroup>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none"><Icon as={FiLock} color="gray.400" /></InputLeftElement>
                    <Input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} />
                  </InputGroup>
                </>
              ) : (
                <VStack spacing={3} w="100%">
                  <HStack spacing={1}><Icon as={FiShield} color="purple.500" /><Text fontSize="sm" fontWeight="600" color="purple.600">
                    {useBackupCode ? 'Enter Backup Code' : 'Enter Authenticator Code'}
                  </Text></HStack>
                  {useBackupCode ? (
                    <Input placeholder="8-character backup code" value={mfaCode}
                      onChange={e => setMfaCode(e.target.value.toUpperCase())}
                      fontFamily="mono" textAlign="center" letterSpacing="0.15em" fontWeight="700"
                      maxLength={8} autoFocus />
                  ) : (
                    <HStack justify="center">
                      <PinInput otp size="lg" value={mfaCode} onChange={setMfaCode} autoFocus>
                        <PinInputField /><PinInputField /><PinInputField /><PinInputField /><PinInputField /><PinInputField />
                      </PinInput>
                    </HStack>
                  )}
                  <HStack spacing={2}>
                    <Button size="xs" variant="ghost" onClick={() => { setMfaRequired(false); setMfaCode(''); setUseBackupCode(false); }}>
                      ← Back
                    </Button>
                    <Button size="xs" variant="ghost" color="purple.500"
                      onClick={() => { setUseBackupCode(!useBackupCode); setMfaCode(''); }}>
                      {useBackupCode ? 'Use authenticator' : 'Use backup code'}
                    </Button>
                  </HStack>
                </VStack>
              )}
            </VStack>

            <Button type="submit" w="100%" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
              isLoading={loading} isDisabled={mfaRequired && (useBackupCode ? mfaCode.length < 8 : mfaCode.length < 6)}>
              {mfaRequired ? 'Verify' : 'Sign In'}
            </Button>

            <Link fontSize="xs" color="gray.500" onClick={() => setShowReset(true)} cursor="pointer" _hover={{ color: 'blue.500' }}>
              Forgot password?
            </Link>
          </VStack>
        </Box>
      </Container>
    </Flex>
  );
}
